// @flow
import React, {
  useState,
  useEffect,
} from 'react';
import {
  Info,
} from 'luxon';
import * as R from 'ramda';

import type {
  DateTime,
  Interval,
} from 'luxon';
import type {
  WorklogsGroupedByDateAndIssue,
} from 'chronos-types';

import CalendarDay from './CalendarDay';
import CalendarWorklog from './CalendarWorklog/CalendarWorklog';
import WorklogPlaceholder from './WorklogPlaceholder';
import Scroller from './Scroller';

import {
  StyledComponent,
  CalendarHeader,
  CalendarHeaderItem,
  CalendarBody,
  CalendarBodyWrapper,
} from './styled';

type Props = {
  /**
   * Date range to render
   * */
  date: Interval,
  /**
   * worklogs
   * */
  data: WorklogsGroupedByDateAndIssue,
  /**
   * Array of days selected.
   */
  calendarSelection: Array<DateTime>,
  /**
   * Function that is called whenever the state of the checkbox changes.
   * */
  onSelect: (day: DateTime, e: Event) => void,
  onRemoveWorklog: (worklogId: ID) => void,
  onEditWorklog: (worklogId: ID) => void,
  onAddWorklog: () => void,
  onShowScreenshotsViewer: () => void,
  /**
   * Function that is called after click delete icon.
   * */
  // onDeleteWorklog: { (): void },
  /**
   * Status of data fetching
   * */
  fetching: boolean,
  jiraUrl: string,
};

// Random days for placeholders
const generatePlaceholders = (length) => Array.from(
  {
    length,
  },
  () => Math.floor(Math.random() * 40),
);

const generateCalendarState = (date: any) => {
  const startWeekday = (
    (date.start.weekday + 1) > 7
      ? 0
      : date.start.weekday
  );
  const endWeekday = (
    (date.end.weekday + 1) > 7
      ? 0
      : date.end.weekday
  );
  const daysBefore = startWeekday === 7 ? 0 : startWeekday;
  const days = date.count('days');
  const daysAfter = endWeekday === 7 ? 0 : 6 - endWeekday;

  const calendarDays = [
    ...Array(daysBefore).fill(0).map((_, i) => date.start.minus({ days: i + 1 })).reverse(),
    ...Array(days).fill(0).map((_, i) => date.start.plus({ days: i })),
    ...Array(daysAfter).fill(0).map((_, i) => date.start.plus({ days: days + i })),
  ].map(d => ({
    d,
    isoDate: d.toISODate(),
    dayGroup: d.toFormat('yyyy-MM-dd'),
  }));

  const mask = [
    ...Array(daysBefore).fill(false),
    ...Array(days).fill(true),
    ...Array(daysAfter).fill(false),
  ];

  return ({
    calendarDays,
    placeholderDays: generatePlaceholders(calendarDays.length),
    mask,
  });
};


const Calendar = ({
  data,
  calendarSelection,
  onSelect,
  onRemoveWorklog,
  onEditWorklog,
  onAddWorklog,
  onShowScreenshotsViewer,
  fetching,
  jiraUrl,
  date,
}: Props) => {
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [topScrollVisibility, setTopScrollVisibility] = useState(false);
  const [downScrollVisibility, setDownScrollVisibility] = useState(true);
  const [top, setTop] = useState(false);
  const [down, setDown] = useState(false);
  const [calendarState, setCalendarState] = useState(generateCalendarState(date));

  const onMouseLeave = () => {
    const calendar: HTMLElement | null = document.querySelector('#calendar');
    setOnMouseEnter(false);
    if (calendar) {
      if (calendar.scrollHeight - calendar.scrollTop === calendar.clientHeight) {
        setDown(true);
        setTop(false);
        setTimeout(() => {
          setDownScrollVisibility(false);
          setTopScrollVisibility(true);
        }, 700);
      }
      if (calendar.scrollTop === 0) {
        setDown(false);
        setTop(true);
        setTimeout(() => {
          setDownScrollVisibility(true);
          setTopScrollVisibility(false);
        }, 700);
      }
      if (calendar.scrollTop !== 0 && !downScrollVisibility) {
        setDown(false);
        setTimeout(() => {
          setDownScrollVisibility(true);
        }, 700);
      }
    }
  };

  useEffect(() => {
    setCalendarState(
      generateCalendarState(date),
    );
  }, [`${fetching}_${date.toISO()}`]);

  const onTopChevronClick = () => {
    const calendar: HTMLElement | null = document.querySelector('#calendar');
    if (calendar) {
      calendar.scrollTop = 0;
    }
  };

  const onBottomChevronClick = () => {
    let scrollBottom = 0;
    const calendar: HTMLElement | null = document.querySelector('#calendar');
    if (calendar) {
      scrollBottom = calendar.scrollHeight - calendar.scrollTop;
      calendar.scrollTop = scrollBottom;
    }
  };

  const scrollToBottom = () => {
    setOnMouseEnter(true);
    const calendar: HTMLElement | null = document.querySelector('#calendar');
    if (calendar) {
      const intervalId = setInterval(() => {
        if (onMouseEnter
          && calendar.scrollHeight - calendar.scrollTop !== calendar.clientHeight) {
          calendar.scrollTop += 3;
          return intervalId;
        }
        return clearInterval(intervalId);
      }, 4);
      setTop(false);
      setTimeout(() => {
        setTopScrollVisibility(true);
      }, 700);
    }
  };

  const scrollToTop = () => {
    setOnMouseEnter(true);
    const calendar: HTMLElement | null = document.querySelector('#calendar');
    if (calendar) {
      const intervalId = setInterval(() => {
        if (onMouseEnter && calendar.scrollTop !== 0) {
          calendar.scrollTop -= 3;
          return intervalId;
        }
        return clearInterval(intervalId);
      }, 4);
      setDown(false);
      setTimeout(() => {
        setDownScrollVisibility(true);
      }, 700);
    }
  };

  const renderHeader = (): React.Node => {
    const weekdays = [
      Info.weekdays('short')[Info.weekdays('short').length - 1],
      ...Info.weekdays('short').slice(0, -1),
    ];
    return (
      <CalendarHeader>
        {weekdays.map(weekday => (
          <CalendarHeaderItem
            key={weekday}
            justify="center"
            align="center"
          >
            {weekday}
          </CalendarHeaderItem>
        ))}
      </CalendarHeader>
    );
  };

  const renderDay = mask => (day, i) => {
    const { isScreenshotsExists, ...worklogGroups } = data[day.dayGroup] || {};

    return (
      <CalendarDay
        key={i}
        isScreenshotsExists={isScreenshotsExists}
        selected={!!calendarSelection[day.isoDate]}
        height={calendarState.calendarDays.length === 7 ? '100%' : '228px'}
        date={day.d}
        shadowed={!mask[i]}
        worklogGroups={worklogGroups}
        fetching={fetching}
        onSelect={evt => onSelect(day.d, evt)}
        onAddWorklog={onAddWorklog}
        onShowScreenshotsViewer={onShowScreenshotsViewer}
      >
        {
          (fetching && calendarState.placeholderDays.includes(i))
            ? (
              <>
                <WorklogPlaceholder />
                <WorklogPlaceholder />
                <WorklogPlaceholder />
              </>
            ) : (
              !fetching && R.values(worklogGroups).map(worklogGroup => (
                <CalendarWorklog
                  key={worklogGroup.issue.id}
                  jiraUrl={jiraUrl}
                  worklogGroup={worklogGroup}
                  onAddWorklog={() => {
                    onAddWorklog(
                      day.d,
                      worklogGroup.issue.id,
                    );
                  }}
                  onEditWorklog={onEditWorklog}
                  onRemoveWorklog={onRemoveWorklog}
                  onShowScreenshotsViewer={onShowScreenshotsViewer}
                />
              ))
            )
        }
      </CalendarDay>
    );
  };


  return (
    <StyledComponent>
      {renderHeader()}
      <CalendarBodyWrapper>
        <CalendarBody>
          {calendarState.calendarDays.map(renderDay(calendarState.mask))}
        </CalendarBody>
      </CalendarBodyWrapper>
      {topScrollVisibility && (
        <Scroller
          direction="top"
          position="top"
          onMouseEnter={scrollToTop}
          onMouseLeave={onMouseLeave}
          onChevronClick={onTopChevronClick}
          chevronFadeOut={top}
        />
      )}
      {downScrollVisibility && (
        <Scroller
          direction="down"
          position="down"
          onMouseEnter={scrollToBottom}
          onMouseLeave={onMouseLeave}
          onChevronClick={onBottomChevronClick}
          chevronFadeOut={down}
        />
      )}
    </StyledComponent>
  );
};

export default Calendar;
